import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FiSearch, FiCalendar, FiUsers, FiChevronDown, FiChevronLeft, FiMapPin, FiX, FiArrowLeft } from 'react-icons/fi'
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2'
import { IoAirplaneOutline } from 'react-icons/io5'
import { API_ENDPOINTS, fetchWithAuth } from '../config/api'
import { getSiteSettings, CURRENCIES } from '../utils/siteSettings'
import { trackSearch } from '../utils/analytics'
import CalendarPicker from './CalendarPicker'
import './SearchBar.css'

/**
 * Reusable search bar component — identical to SearchResultsPage's search bar.
 * Renders desktop inline bar + mobile compact pill + mobile edit modal + fullscreen pickers.
 *
 * Props:
 *   initialDestination  – string, pre-filled destination text
 *   initialCheckin      – string 'YYYY-MM-DD'
 *   initialCheckout     – string 'YYYY-MM-DD'
 *   initialRooms        – array of { adults, children, childAges }
 *   vacationRentals     – bool, append vacationRentals=true to URL
 *   fallbackLatLng      – { lat, lng } used when user doesn't pick a suggestion
 *   className           – extra class for the wrapper
 */
export default function SearchBar({
  initialDestination = '',
  initialLocation = '',
  initialState = '',
  initialCountry = '',
  initialPlaceId = '',
  initialLatitude = '',
  initialLongitude = '',
  initialCheckin = '',
  initialCheckout = '',
  initialRooms,
  vacationRentals = false,
  fallbackLatLng,
  className = '',
  hideDestination = false,
  onSearch,
}) {
  const navigate = useNavigate()
  const location = useLocation()

  // ─── State ───
  const [showMobileSearch, setShowMobileSearch] = useState(false)

  const resolveCountryName = (code = '') => {
    if (!code || !/^[A-Za-z]{2}$/.test(code.trim())) return code
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.trim().toUpperCase()) || code
    } catch {
      return code
    }
  }

  const buildDestinationDisplay = (name, state, country) => {
    const resolvedCountry = resolveCountryName(country)
    const isUS = country && /^us$/i.test(country.trim())
    // US places: show "City, State" (e.g. "Kona, Hawaii")
    if (isUS && state && name && state.toLowerCase() !== name.toLowerCase()) {
      return `${name}, ${state}`
    }
    // International: show "City, Country" (e.g. "Paris, France")
    return resolvedCountry && name && resolvedCountry.toLowerCase() !== name.toLowerCase()
      ? `${name}, ${resolvedCountry}`
      : name
  }

  const [destination, setDestination] = useState(() => buildDestinationDisplay(initialDestination, initialState, initialCountry))
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState(null)
  const searchTimeout = useRef(null)
  const searchCache = useRef({})
  const whereRef = useRef(null)

  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const dateRef = useRef(null)

  const [showGuestPicker, setShowGuestPicker] = useState(false)
  const [roomsList, setRoomsList] = useState([{ adults: 2, children: 0, childAges: [] }])
  const guestRef = useRef(null)

  // ─── Sync props → state on mount / change ───
  useEffect(() => {
    setDestination(buildDestinationDisplay(initialDestination, initialState, initialCountry))
  }, [initialDestination, initialState, initialCountry])

  useEffect(() => {
    const parse = s => {
      if (!s) return null
      const [y, m, d] = s.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    setDateRange({ startDate: parse(initialCheckin), endDate: parse(initialCheckout) })
  }, [initialCheckin, initialCheckout])

  useEffect(() => {
    if (initialRooms && initialRooms.length > 0) setRoomsList(initialRooms)
  }, [initialRooms])

  // ─── Outside-click handler (desktop) ───
  useEffect(() => {
    const handler = (e) => {
      if (showMobileSearch) return
      if (whereRef.current && !whereRef.current.contains(e.target)) setShowSuggestions(false)
      if (dateRef.current && !dateRef.current.contains(e.target)) setShowDatePicker(false)
      if (guestRef.current && !guestRef.current.contains(e.target)) setShowGuestPicker(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMobileSearch])

  // ─── Places autocomplete ───
  const fetchPlaces = async (query) => {
    if (query.length < 3) return setSuggestions([])
    const key = query.toLowerCase().trim()
    if (searchCache.current[key]) { setSuggestions(searchCache.current[key]); return }
    setLoadingSuggestions(true)
    try {
      const resp = await fetchWithAuth(`${API_ENDPOINTS.places}?textQuery=${encodeURIComponent(query)}&language=en`)
      if (!resp.ok) throw new Error('API error')
      const data = await resp.json()
      const places = (data.data || data.places || []).slice(0, 6)
      searchCache.current[key] = places
      setSuggestions(places)
    } catch (e) { console.warn('Places API error:', e) }
    setLoadingSuggestions(false)
  }

  const handleInputChange = (e) => {
    const text = e.target.value
    setDestination(text)
    setSelectedPlace(null)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    if (text.length >= 3) {
      searchTimeout.current = setTimeout(() => fetchPlaces(text), 500)
      setShowSuggestions(true)
    } else { setSuggestions([]); setShowSuggestions(false) }
  }

  const handleSelectPlace = (place) => {
    const baseName = place.displayName || place.name || ''
    const formattedAddress = place.formattedAddress || ''
    const baseLower = baseName.toLowerCase()
    const formattedLower = formattedAddress.toLowerCase()

    let displayText = baseName
    if (!isHotelOrLodging(place) && formattedAddress) {
      displayText = formattedLower.includes(baseLower)
        ? formattedAddress
        : `${baseName}, ${formattedAddress}`
    }

    setDestination(displayText)
    setSelectedPlace(place)
    setSuggestions([])
    setShowSuggestions(false)
  }

  const isHotelOrLodging = (place) => {
    if (!place?.types) return false
    return place.types.some(t => {
      if (!t) return false
      const l = t.toLowerCase()
      return l.includes('hotel') || l.includes('lodging')
    })
  }

  const normalizeTownOrCityName = (name = '', formattedAddress = '') => {
    const cleanToken = (value = '') => value
      .replace(/\b(regency|province|state|county|district|region)\b/gi, '')
      .replace(/,\s*[A-Z]{2}$/, '')
      .replace(/\s{2,}/g, ' ')
      .trim()

    const primary = cleanToken(name)
    if (primary && primary.length >= 2) return primary

    const parts = String(formattedAddress)
      .split(',')
      .map(p => cleanToken(p))
      .filter(Boolean)

    if (parts.length === 0) return ''

    const blocked = /(indonesia|united states|usa|uk|england|australia|canada)$/i
    const best = parts.find(p => !blocked.test(p)) || parts[0]
    return best
  }

  // ─── Room helpers ───
  const updateRoom = (idx, field, value) => {
    setRoomsList(rooms => rooms.map((r, i) => {
      if (i !== idx) return r
      if (field === 'children') return { ...r, children: value, childAges: Array(value).fill(1) }
      return { ...r, [field]: value }
    }))
  }

  const updateChildAge = (roomIdx, childIdx, age) => {
    setRoomsList(rooms => rooms.map((r, i) => {
      if (i !== roomIdx) return r
      const ages = [...r.childAges]
      ages[childIdx] = age
      return { ...r, childAges: ages }
    }))
  }

  // ─── Computed display values ───
  const nightsCount = (() => {
    if (!dateRange.startDate || !dateRange.endDate) return 0
    return Math.ceil((dateRange.endDate - dateRange.startDate) / 86400000)
  })()

  const guestSummary = (() => {
    const r = roomsList.length
    const g = roomsList.reduce((s, rm) => s + rm.adults + rm.children, 0)
    return `${r} Room${r > 1 ? 's' : ''}, ${g} Guest${g > 1 ? 's' : ''}`
  })()

  const dateDisplay = (() => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Add dates'
    const fmt = d => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
    return `${fmt(dateRange.startDate)} - ${fmt(dateRange.endDate)}${nightsCount > 0 ? ` (${nightsCount} night${nightsCount > 1 ? 's' : ''})` : ''}`
  })()

  const dateRangeText = (() => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Add dates'
    const fmt = d => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
    return `${fmt(dateRange.startDate)} - ${fmt(dateRange.endDate)}`
  })()

  const formatDateDisplay = useCallback((short = false) => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Add dates'
    if (short) {
      const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      return `${fmt(dateRange.startDate)} – ${fmt(dateRange.endDate)}`
    }
    return dateDisplay
  }, [dateRange, dateDisplay])

  // ─── Search handler ───
  const handleSearch = () => {
    const fmtDate = d => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : ''
    const ci = fmtDate(dateRange.startDate) || initialCheckin
    const co = fmtDate(dateRange.endDate) || initialCheckout
    const adultsTotal = roomsList.reduce((s, r) => s + r.adults, 0)
    const childrenParam = roomsList.reduce((acc, r) => acc.concat(r.childAges || []), []).filter(a => a != null).join(',')

    if (onSearch) {
      onSearch({ checkin: ci, checkout: co, adults: adultsTotal, rooms: roomsList.length, children: childrenParam })
      setShowMobileSearch(false)
      return
    }

    const place = selectedPlace
    const params = new URLSearchParams()
    const destinationUnchanged =
      destination.trim() === (initialDestination || '').trim() ||
      destination.trim() === buildDestinationDisplay(initialDestination, initialState, initialCountry).trim()

    if (place) {
      if (isHotelOrLodging(place)) {
        const lat = place.location?.latitude || place.geometry?.location?.lat
        const lng = place.location?.longitude || place.geometry?.location?.lng
        if (lat && lng) {
          params.set('latitude', String(lat))
          params.set('longitude', String(lng))
          params.set('name', place.displayName || place.name || '')
          params.set('matchHotelName', place.displayName || place.name || '')
        }
      } else {
        if (place.placeId || place.id) params.set('placeId', place.placeId || place.id)
        const placeName = place.displayName || place.name || ''
        const formattedAddress = place.formattedAddress || ''
        const townOrCityName = normalizeTownOrCityName(placeName, formattedAddress) || placeName
        params.set('name', townOrCityName)
        if (formattedAddress) {
          params.set('location', formattedAddress)
          const countryPart = formattedAddress.split(',').map(s => s.trim()).at(-1)
          if (countryPart) params.set('country', resolveCountryName(countryPart))
        }
      }
    } else if (destination) {
      if (fallbackLatLng?.lat && fallbackLatLng?.lng) {
        params.set('latitude', String(fallbackLatLng.lat))
        params.set('longitude', String(fallbackLatLng.lng))
      }
      params.set('name', destinationUnchanged && initialDestination ? initialDestination : destination)
      if (destinationUnchanged) {
        if (initialPlaceId) params.set('placeId', initialPlaceId)
        if (initialLatitude && initialLongitude) {
          params.set('latitude', String(initialLatitude))
          params.set('longitude', String(initialLongitude))
        }
        if (initialLocation) params.set('location', initialLocation)
        if (initialState) params.set('state', initialState)
        if (initialCountry) params.set('country', resolveCountryName(initialCountry))
      }
    } else {
      if (fallbackLatLng?.lat && fallbackLatLng?.lng) {
        params.set('latitude', String(fallbackLatLng.lat))
        params.set('longitude', String(fallbackLatLng.lng))
      }
      params.set('name', initialDestination || 'Nearby')
      if (initialPlaceId) params.set('placeId', initialPlaceId)
      if (initialLatitude && initialLongitude) {
        params.set('latitude', String(initialLatitude))
        params.set('longitude', String(initialLongitude))
      }
      if (initialLocation) params.set('location', initialLocation)
      if (initialCountry) params.set('country', resolveCountryName(initialCountry))
    }

    if (!params.has('name') && !params.has('placeId') && !params.has('latitude')) return

    if (ci) params.set('checkin', ci)
    if (co) params.set('checkout', co)
    params.set('rooms', String(roomsList.length))
    params.set('adults', String(adultsTotal))
    if (childrenParam) params.set('children', childrenParam)
    const urlCurrency = new URLSearchParams(location.search).get('currency')?.toUpperCase() || ''
    const currency = CURRENCIES.some(c => c.code === urlCurrency) ? urlCurrency : getSiteSettings().currency
    if (currency) params.set('currency', currency)
    if (vacationRentals) params.set('vacationRentals', 'true')
    const searchBasePath = '/hotels'

    trackSearch({
      searchTerm: params.get('name') || destination || initialDestination || '',
      category: vacationRentals ? 'vacation_rental' : 'hotel',
      checkin: ci,
      checkout: co,
      adults: adultsTotal,
      rooms: roomsList.length,
      currency,
    })

    navigate(`${searchBasePath}?${params.toString()}`)
  }

  // ─── Place icon helper ───
  const placeIcon = (place) => {
    if (place.types?.some(t => (t || '').toLowerCase().includes('hotel') || (t || '').toLowerCase().includes('lodging')))
      return <HiOutlineBuildingOffice2 size={18} />
    if (place.types?.some(t => (t || '').toLowerCase().includes('airport')))
      return <IoAirplaneOutline size={18} />
    return <FiMapPin size={18} />
  }

  // ═══════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════
  return (
    <div className={`searchbar-component ${className}`}>

      {/* ═══ DESKTOP BAR ═══ */}
      <div className="results-header searchbar-desktop-only">
        <div className="results-header-content">
          <div className="search-bar-container">
            {/* Where To */}
            {!hideDestination && (
            <div className="search-field where-field" ref={whereRef}>
              <label>Where to?</label>
              <div className="field-input-wrapper">
                <FiSearch size={16} className="field-icon" />
                <input
                  type="text"
                  placeholder="Search destinations"
                  value={destination}
                  onChange={handleInputChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                />
              </div>
              {showSuggestions && (suggestions.length > 0 || loadingSuggestions) && (
                <div className="search-dropdown">
                  {loadingSuggestions ? (
                    <div className="dropdown-loading">Searching...</div>
                  ) : suggestions.map((place, i) => (
                    <div key={place.placeId || i} className="dropdown-item"
                      onMouseDown={e => { e.preventDefault(); handleSelectPlace(place) }}>
                      <div className="place-icon">{placeIcon(place)}</div>
                      <div className="place-info">
                        <span className="place-name">{place.displayName || place.name}</span>
                        <span className="place-address">{place.formattedAddress || ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {!hideDestination && <div className="search-divider" />}

            {/* Dates */}
            <div className="search-field dates-field" ref={dateRef}>
              <label>Check-in - Check-out</label>
              <button className="field-button dates-button" onClick={() => { setShowDatePicker(!showDatePicker); setShowGuestPicker(false) }}>
                <FiCalendar size={16} className="field-icon" />
                <span className="dates-text-wrap">
                  <span className="dates-main">{dateRangeText}</span>
                  {nightsCount > 0 && <span className="dates-nights">({nightsCount} night{nightsCount > 1 ? 's' : ''})</span>}
                </span>
              </button>
              {showDatePicker && (
                <div className="search-dropdown date-dropdown">
                  <div className="date-dropdown-header">
                    <span className="date-range-display">{dateDisplay}</span>
                    {nightsCount > 0 && (
                      <span className="nights-count">{nightsCount} Night{nightsCount > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <CalendarPicker
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onDateChange={(s, e) => setDateRange({ startDate: s, endDate: e })}
                    mode="side-by-side"
                    onClose={() => setShowDatePicker(false)}
                  />
                </div>
              )}
            </div>

            <div className="search-divider" />

            {/* Guests */}
            <div className="search-field guests-field" ref={guestRef}>
              <label>Rooms & Guests</label>
              <button className="field-button guests-button" onClick={() => { setShowGuestPicker(!showGuestPicker); setShowDatePicker(false) }}>
                <FiUsers size={16} className="field-icon" />
                <span>{guestSummary}</span>
                <FiChevronDown size={14} className="chevron-icon" />
              </button>
              {showGuestPicker && (
                <div className="search-dropdown guest-dropdown">
                  <div className="room-block">
                    <div className="guest-row">
                      <span>Adults</span>
                      <div className="stepper">
                        <button onClick={() => updateRoom(0, 'adults', Math.max(1, roomsList[0].adults - 1))} disabled={roomsList[0].adults <= 1}>-</button>
                        <span>{roomsList[0].adults}</span>
                        <button onClick={() => updateRoom(0, 'adults', Math.min(8, roomsList[0].adults + 1))} disabled={roomsList[0].adults >= 8}>+</button>
                      </div>
                    </div>
                    <div className="guest-row">
                      <span>Children</span>
                      <div className="stepper">
                        <button onClick={() => updateRoom(0, 'children', Math.max(0, roomsList[0].children - 1))} disabled={roomsList[0].children <= 0}>-</button>
                        <span>{roomsList[0].children}</span>
                        <button onClick={() => updateRoom(0, 'children', Math.min(6, roomsList[0].children + 1))} disabled={roomsList[0].children >= 6}>+</button>
                      </div>
                    </div>
                    {roomsList[0].children > 0 && (
                      <div className="child-ages">
                        {roomsList[0].childAges.map((age, ci) => (
                          <div key={ci} className="child-age-row">
                            <span>Child {ci + 1} age</span>
                            <select value={age} onChange={e => updateChildAge(0, ci, Number(e.target.value))}>
                              <option value={0}>Under 1</option>
                              {[...Array(17)].map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!vacationRentals && (
                    <button className="dropdown-done-btn" onClick={() => setShowGuestPicker(false)}>Done</button>
                  )}
                </div>
              )}
            </div>
            <button className="update-search-btn" onClick={handleSearch}><FiSearch size={18} strokeWidth={2.5} />Search</button>
          </div>
        </div>
      </div>

      {/* ═══ MOBILE COMPACT BAR ═══ */}
      <div className="mobile-search-bar searchbar-mobile-only">
        <div className="mobile-search-content" onClick={() => setShowMobileSearch(true)}>
          <FiSearch size={18} className="mobile-search-icon" />
          <div className="mobile-search-info">
            {!hideDestination && <span className="mobile-search-location">{destination || 'Search'}</span>}
            <span className={hideDestination ? "mobile-search-location" : "mobile-search-dates"}>
              {formatDateDisplay(true)} {'\u00B7'} {guestSummary}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ MOBILE EDIT SEARCH MODAL ═══ */}
      {showMobileSearch && !showDatePicker && !showGuestPicker && (
        <div className="mobile-search-modal-overlay">
          <div className="mobile-search-modal">
            <div className="mobile-search-modal-header">
              <button className="mobile-modal-close" onClick={() => setShowMobileSearch(false)}>
                <FiX size={24} />
              </button>
            </div>
            <div className="mobile-modal-fields">
              {!hideDestination && (
              <div className="mobile-modal-field">
                <label><FiSearch size={16} /> Where to?</label>
                <input
                  type="text"
                  placeholder="Search destinations"
                  value={destination}
                  onChange={handleInputChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                />
                {showSuggestions && (suggestions.length > 0 || loadingSuggestions) && (
                  <div className="mobile-destination-results" style={{ padding: 0 }}>
                    {loadingSuggestions ? (
                      <div className="mobile-destination-loading">Searching...</div>
                    ) : suggestions.map((place, i) => (
                      <div key={place.placeId || i} className="mobile-destination-item"
                        onMouseDown={e => { e.preventDefault(); handleSelectPlace(place) }}>
                        <div className="mobile-destination-icon">{placeIcon(place)}</div>
                        <div className="mobile-destination-info">
                          <span className="mobile-destination-name">{place.displayName || place.name}</span>
                          <span className="mobile-destination-address">{place.formattedAddress || ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              <div className="mobile-modal-field">
                <label><FiCalendar size={16} /> Check-in - Check-out</label>
                <button className="mobile-modal-field-btn" onClick={() => setShowDatePicker(true)}>
                  {dateDisplay}
                </button>
              </div>

              <div className="mobile-modal-field">
                <label><FiUsers size={16} /> Rooms & Guests</label>
                <button className="mobile-modal-field-btn" onClick={() => setShowGuestPicker(true)}>
                  {guestSummary}
                  <FiChevronDown size={16} />
                </button>
              </div>

              <button className="mobile-update-search-btn" onClick={() => { setShowMobileSearch(false); handleSearch() }}>
                <FiSearch size={20} /> Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MOBILE FULLSCREEN DATE PICKER ═══ */}
      {showDatePicker && showMobileSearch && (
        <div className="mobile-fullscreen-picker">
          <div className="mobile-picker-header">
            <button className="mobile-picker-back" onClick={() => setShowDatePicker(false)}>
              <FiArrowLeft size={24} />
            </button>
            <div className="mobile-picker-header-content">
              <span className="mobile-picker-title">Select Dates</span>
              <span className="mobile-picker-subtitle">
                {dateRange.startDate && dateRange.endDate
                  ? `${dateDisplay}`
                  : 'Choose your check-in and check-out dates'}
              </span>
            </div>
            <div style={{ width: 40 }} />
          </div>
          <div className="mobile-calendar-wrapper">
            <CalendarPicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onDateChange={(s, e) => setDateRange({ startDate: s, endDate: e })}
              mode="scroll"
            />
          </div>
          <div className="mobile-picker-footer">
            <button className="mobile-picker-done" onClick={() => setShowDatePicker(false)}>Done</button>
          </div>
        </div>
      )}

      {/* ═══ MOBILE FULLSCREEN GUEST PICKER ═══ */}
      {showGuestPicker && showMobileSearch && (
        <div className="mobile-fullscreen-picker">
          <div className="mobile-picker-header">
            <button className="mobile-picker-back" onClick={() => setShowGuestPicker(false)}>
              <FiArrowLeft size={24} />
            </button>
            <span className="mobile-picker-title">Rooms & Guests</span>
            <div style={{ width: 24 }} />
          </div>
          <div className="mobile-picker-content">
            <div className="room-block">
              <div className="guest-row">
                <span>Adults</span>
                <div className="stepper">
                  <button onClick={() => updateRoom(0, 'adults', Math.max(1, roomsList[0].adults - 1))} disabled={roomsList[0].adults <= 1}>-</button>
                  <span>{roomsList[0].adults}</span>
                  <button onClick={() => updateRoom(0, 'adults', Math.min(8, roomsList[0].adults + 1))} disabled={roomsList[0].adults >= 8}>+</button>
                </div>
              </div>
              <div className="guest-row">
                <span>Children</span>
                <div className="stepper">
                  <button onClick={() => updateRoom(0, 'children', Math.max(0, roomsList[0].children - 1))} disabled={roomsList[0].children <= 0}>-</button>
                  <span>{roomsList[0].children}</span>
                  <button onClick={() => updateRoom(0, 'children', Math.min(6, roomsList[0].children + 1))} disabled={roomsList[0].children >= 6}>+</button>
                </div>
              </div>
              {roomsList[0].children > 0 && (
                <div className="child-ages">
                  {roomsList[0].childAges.map((age, ci) => (
                    <div key={ci} className="child-age-row">
                      <span>Child {ci + 1} age</span>
                      <select value={age} onChange={e => updateChildAge(0, ci, Number(e.target.value))}>
                        <option value={0}>Under 1</option>
                        {[...Array(17)].map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mobile-picker-footer">
            {!vacationRentals && (
              <button className="mobile-picker-done" onClick={() => setShowGuestPicker(false)}>Done</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
